import {} from "viem";
import {} from "../../account/smartContractAccount.js";
import { isBaseSmartAccountClient } from "../../client/isSmartAccountClient.js";
import { AccountNotFoundError } from "../../errors/account.js";
import { IncompatibleClientError } from "../../errors/client.js";
import { _initUserOperation } from "./internal/initUserOperation.js";
import { _runMiddlewareStack } from "./internal/runMiddlewareStack.js";
import { clientHeaderTrack } from "../../client/addBreadcrumb.js";
const USER_OPERATION_METHOD = "buildUserOperation";
/**
 * Builds a user operation using the provided client and operation parameters. Ensures that the account exists and the client is compatible.
 *
 * @example
 * ```ts
 * import { createSmartAccountClient } from "@aa-sdk/core";
 *
 * // smart account client is already extended with buildUserOperation
 * const client = createSmartAccountClient(...);
 * const result = await client.buildUserOperation({
 *  uo: {
 *    target: "0x...",
 *    data: "0x...", // or "0x",
 *    value: 0n, // optional
 *  },
 *  account, // only required if the client above is not connected to an account
 * });
 * ```
 *
 * @param {Client<TTransport, TChain, TAccount>} client_ the client instance used to build the user operation
 * @param {BuildUserOperationParameters<TAccount, TContext, TEntryPointVersion>} args the parameters required to build the user operation, including account, overrides, and context
 * @returns {Promise<UserOperationStruct<TEntryPointVersion>>} a promise that resolves to a `UserOperationStruct` object containing the built user operation details
 */
export async function buildUserOperation(client_, args) {
    const client = clientHeaderTrack(client_, USER_OPERATION_METHOD);
    const { account = client.account, overrides, context } = args;
    if (!account) {
        throw new AccountNotFoundError();
    }
    if (!isBaseSmartAccountClient(client)) {
        throw new IncompatibleClientError("BaseSmartAccountClient", USER_OPERATION_METHOD, client);
    }
    return _initUserOperation(client, args).then((_uo) => _runMiddlewareStack(client, {
        uo: _uo,
        overrides,
        account,
        context,
    }));
}
//# sourceMappingURL=buildUserOperation.js.map